/**
 * THE THRESHOLD — what stands between opening the page and waking in a world.
 *
 * `DESIGN.md` §1.1 and the README both say the same thing: *you wake in a world
 * that was here before you*. Nothing used to sit between load and world, and
 * that was right while there was only one world to wake in. Once a world can be
 * **hosted** there is a question that has to be answered before anything can be
 * drawn — *which* world, and on whose authority — and a game that guessed would
 * be guessing about a thing that cannot be changed afterwards.
 *
 * So this is a threshold and deliberately not a lobby. It does not list
 * servers, it has no news panel, it does not pause anything, and it asks
 * exactly two questions: your own world or one that is being kept, and if the
 * latter, where. Everything else on it is a *reading of the world you are
 * about to enter* — its age, its people, the gods already in it — because that
 * reading is the whole argument for a persistent world and it is worth making
 * before the first frame rather than after.
 *
 * ## It is drawn over a world that is already running
 *
 * Exactly like `ui/menu.ts`, and for the same reason (`D §1.1`: the world does
 * not wait). The renderer, the simulation and the clock are all live behind
 * the sheet. Choosing "your own world" does not *start* anything — it lifts the
 * sheet off something that was already there.
 *
 * ## What it must never do
 *
 * **It must never appear for a capture station or a probe.** `CLAUDE.md`'s
 * visual gate is `?cap=camp` and `?cap=far` in a browser, and a sheet over the
 * frame would break every screenshot the project judges itself by — silently,
 * and in the one place where a silent failure costs the most. `SKIPPED_BY`
 * below is that guard, it names every dev door the game answers to, and
 * `test/threshold.ts` is what keeps it honest when somebody adds the next one.
 *
 * It writes nothing to any world (law 9). What it remembers — the last server,
 * the last name — is a browser preference, kept beside the world and never in
 * it, on the same argument `main.ts` makes for its wall-clock stamp.
 */

import { MAX_NAME_LENGTH, cleanName } from '@/host/intent';
import type { AttachRequest } from './attach';

/**
 * URL parameters that mean "this is not a play session".
 *
 * Every one of these is a door that a tool, a probe or the screenshot gate
 * opens, and each of them wants the world and nothing over it. `server` +
 * `token` is here for a different reason: a friend who was handed a direct
 * link has already made the only choice the threshold exists to ask about, and
 * making them confirm it would be a dialog for its own sake.
 */
const SKIPPED_BY: readonly string[] = [
  'cap',      // the capture stations — CLAUDE.md's visual gate
  'probe',    // the capture harness's own frame pump
  'pheno',    // the phenotype lab
  'burn',     // the burn rig
  'bless',    // the blessing rig
  'days',     // the dev time door
  'tod',      // a pinned time of day, only ever passed with `cap`
  'aura',     // the aura's radius — the detail ring, driven for testing
];

/**
 * True when the URL has already decided, so the threshold stays out of the way.
 *
 * Exported and pure so the guard can be tested without a DOM — the thing being
 * protected is a screenshot gate that no unit test can see.
 */
export function thresholdSkipped(search: string): boolean {
  const q = new URLSearchParams(search);
  if (SKIPPED_BY.some((k) => q.has(k))) return true;
  // A complete direct link: the choice has been made by whoever sent it.
  if (q.get('server') && q.get('token')) return true;
  return false;
}

/** What the player chose. */
export type Entry =
  | { kind: 'local' }
  | { kind: 'join'; request: AttachRequest };

/** What the threshold could learn about a world before anyone entered it. */
interface Reading {
  ok: boolean;
  why?: string;
  worldId?: string;
  label?: string;
  worldTimeText?: string;
  years?: number;
  souls?: number;
  peoples?: number;
  structures?: number;
  gods?: Array<{ deityId: string; name?: string }>;
  paused?: boolean;
  lastSaved?: string;
}

const REMEMBER = 'unseen-threshold';

interface Remembered { origin?: string; token?: string; name?: string }

const recall = (): Remembered => {
  try { return JSON.parse(localStorage.getItem(REMEMBER) ?? '{}') as Remembered; }
  catch { return {}; }
};
const remember = (r: Remembered): void => {
  try { localStorage.setItem(REMEMBER, JSON.stringify(r)); } catch { /* private mode */ }
};

/** `8787` and `192.168.1.8:8787` are both things a person types. */
export function originFrom(raw: string, page = location): string {
  const s = raw.trim().replace(/\/+$/, '');
  if (!s) return '';
  if (/^\d+$/.test(s)) return `${page.protocol}//${page.hostname}:${s}`;
  return /^https?:\/\//.test(s) ? s : `http://${s}`;
}

/**
 * Ask a world what it is, before entering it.
 *
 * Two requests, and the split matters for what can be said when it fails.
 * `/health` needs no token, so it separates *there is no server there* from
 * *your token is wrong* — which are the two mistakes a person actually makes,
 * and telling them apart is most of what a join screen is for.
 */
export async function readWorld(origin: string, token: string): Promise<Reading> {
  const at = (path: string) => `${origin}${path}`;
  try {
    const health = await fetch(at('/health'));
    if (!health.ok) return { ok: false, why: `${origin} answered ${health.status}` };
  } catch {
    return { ok: false, why: `nothing is listening at ${origin}` };
  }
  let res: Response;
  try {
    res = await fetch(at(`/world/status?token=${encodeURIComponent(token)}`));
  } catch (e) {
    return { ok: false, why: (e as Error).message };
  }
  if (res.status === 401) return { ok: false, why: 'that is not the right token' };
  if (!res.ok) return { ok: false, why: `the world answered ${res.status}` };
  const s = await res.json() as {
    worldId: string; label?: string; worldTimeText: string; worldTime: number;
    paused: boolean; census: { people: number; cultures: number; structures: number };
    connected?: Array<{ deityId: string; name?: string }>;
    latestCheckpoint?: { ageRealSeconds: number };
  };
  const age = s.latestCheckpoint?.ageRealSeconds;
  return {
    ok: true,
    worldId: s.worldId,
    ...(s.label ? { label: s.label } : {}),
    worldTimeText: s.worldTimeText,
    years: s.worldTime / (86400 * 365),
    souls: Math.round(s.census.people),
    peoples: s.census.cultures,
    structures: s.census.structures,
    gods: s.connected ?? [],
    paused: s.paused,
    ...(age !== undefined ? { lastSaved: agoText(age) } : {}),
  };
}

const agoText = (seconds: number): string =>
  seconds < 90 ? `${Math.round(seconds)}s ago`
  : seconds < 5400 ? `${Math.round(seconds / 60)}m ago`
  : seconds < 172800 ? `${(seconds / 3600).toFixed(1)}h ago`
  : `${(seconds / 86400).toFixed(1)}d ago`;

/**
 * Is the page I am looking at being served BY a world server?
 *
 * When the world server carries the built game — which is how this is meant to
 * be hosted — the answer is yes, and it changes what the threshold should say.
 * A player who typed `world.example` into a browser is there to join *that*
 * world; making them then type its address back to it would be asking a
 * question whose answer is already on the screen.
 *
 * The check has to be more than "did `/health` answer", because a dev server
 * hands back the app's own index.html for any unknown path and that is a 200.
 * A world server answers with JSON naming a protocol version, and nothing else
 * this page could be served from does.
 */
export async function servedByWorld(origin = location.origin): Promise<boolean> {
  try {
    const res = await fetch(`${origin}/health`);
    if (!res.ok) return false;
    const body = await res.json() as { ok?: boolean; protocolVersion?: number };
    return body?.ok === true && typeof body.protocolVersion === 'number';
  } catch { return false; }
}

/**
 * Put the threshold up and resolve when the player has crossed it.
 *
 * Resolves exactly once. There is no cancel: the only two ways out are the two
 * answers, which is what "a threshold rather than a dialog" means.
 */
export interface ThresholdOptions {
  /**
   * Called the first time the player looks at "your own world".
   *
   * The century is 3.8 MB and the caller wants it downloading while the sheet
   * is being read — but only if it is going to be used. A player joining a
   * hosted world would otherwise pull down a world they never enter, which on
   * somebody's home upload is most of a minute of their bandwidth spent on
   * nothing.
   */
  onWantOwnWorld?: () => void;
}

export function crossThreshold(opts: ThresholdOptions = {}): Promise<Entry> {
  const style = document.createElement('style');
  /*
   * The same hand as `ui/menu.ts` — a serif display line, a small-caps
   * subtitle at wide tracking, mono for everything factual, actions that are
   * words with a hairline under them rather than pills. Two departures, both
   * deliberate:
   *
   *   · the backdrop is heavier than the menu's (.86 against .58) and blurred
   *     harder. The menu is a page held over a world you are IN and must be
   *     able to see; this is the moment before, and the world behind it should
   *     read as somewhere you have not arrived yet.
   *   · the reading of the world is a definition list in mono, right-aligned
   *     on its values, because it is a set of measurements and measurements
   *     line up. It is the only place in the chrome that does, and that is the
   *     point: this screen's job is to be *specific* about what is on the
   *     other side.
   */
  style.textContent = `
    #threshold { position:fixed; inset:0; z-index:90; display:flex;
      align-items:center; justify-content:center;
      background:rgba(10,11,9,.86); backdrop-filter:blur(9px);
      -webkit-backdrop-filter:blur(9px);
      opacity:0; transition:opacity .5s var(--ease); }
    #threshold.in { opacity:1; }
    #threshold.out { opacity:0; pointer-events:none; }
    #threshold .sheet { width:min(560px,92vw); max-height:90vh; overflow-y:auto;
      scrollbar-width:thin;
      padding:30px 40px 26px; background:var(--pane); border:1px solid var(--edge);
      border-radius:3px; box-shadow:var(--lift), inset 0 1px 0 rgba(233,227,210,.06);
      position:relative; opacity:0; transform:translateY(11px);
      transition:opacity .44s var(--ease) .12s, transform .44s var(--ease) .12s; }
    #threshold.in .sheet { opacity:1; transform:none; }
    #threshold .sheet::before { content:''; position:absolute; top:11px; left:11px;
      width:16px; height:16px; border:1px solid var(--gold-deep); border-right:0;
      border-bottom:0; opacity:.5; }
    #threshold .sheet::after { content:''; position:absolute; bottom:11px; right:11px;
      width:16px; height:16px; border:1px solid var(--gold-deep); border-left:0;
      border-top:0; opacity:.5; }
    #threshold h1 { font:italic 400 33px/1 var(--serif); color:var(--ink);
      letter-spacing:.05em; }
    #threshold .sub { margin-top:8px; font-size:9px; letter-spacing:.3em;
      text-transform:uppercase; color:var(--dim); }
    #threshold .rule { height:1px; margin:20px 0 17px;
      background:linear-gradient(90deg,transparent,var(--line) 12%,
        var(--line) 88%,transparent); }
    #threshold .ways { display:flex; gap:26px; margin-bottom:16px; }
    #threshold .ways button { padding:4px 1px 6px; font-size:10px; letter-spacing:.2em;
      text-transform:uppercase; color:var(--dim);
      border-bottom:1px solid transparent; transition:color .18s, border-color .18s; }
    #threshold .ways button:hover { color:var(--gold); }
    #threshold .ways button.on { color:var(--ink); border-bottom-color:var(--gold); }
    #threshold .blurb { font:italic 400 14px/1.7 var(--serif); color:var(--dim);
      margin-bottom:16px; }
    #threshold .blurb em { color:var(--gold); font-style:italic; }
    #threshold label { display:block; font-size:9px; letter-spacing:.24em;
      text-transform:uppercase; color:var(--dim); margin-bottom:6px; }
    #threshold .field { margin-bottom:12px; }
    #threshold input { width:100%; padding:8px 10px; font:11px/1.4 var(--mono);
      color:var(--ink); background:rgba(233,227,210,.05);
      border:1px solid var(--line); border-radius:2px; outline:none;
      transition:border-color .18s; }
    #threshold input:focus { border-color:var(--gold-deep); }
    #threshold .pair { display:grid; grid-template-columns:1fr 1fr; gap:0 16px; }
    #threshold .reading { margin:14px 0 2px; }
    #threshold .reading table { width:100%; border-collapse:collapse; }
    #threshold .reading td { font:10px/1.9 var(--mono); color:var(--dim);
      padding:1px 0; }
    #threshold .reading td.v { text-align:right; color:var(--ink); }
    #threshold .reading .head { font:italic 400 15px/1.4 var(--serif);
      color:var(--gold); margin-bottom:7px; }
    #threshold .bad { font:11px/1.6 var(--mono); color:var(--dear); margin:16px 0 2px; }
    #threshold .go { margin-top:18px; display:flex; align-items:baseline;
      justify-content:space-between; gap:18px; }
    #threshold .go button { padding:5px 1px 7px; font:11px/1 var(--mono);
      letter-spacing:.24em; text-transform:uppercase; color:var(--ink);
      border-bottom:1px solid var(--gold); transition:color .18s, opacity .18s; }
    #threshold .go button:hover { color:var(--gold); }
    #threshold .go button[disabled] { opacity:.35; border-bottom-color:var(--line);
      cursor:default; }
    #threshold .hint { font:10px/1.6 var(--mono); color:var(--dim); opacity:.8; }
    @media (max-width:560px) { #threshold .pair { grid-template-columns:1fr; } }
    /*
     * A SHORT WINDOW, which is most laptops and every browser pane.
     *
     * At 450px of height the sheet was scrolling, and a threshold you have to
     * scroll to find the way out of is a bad threshold. What goes is the
     * blurb: it is flavour and the reading underneath it is information, and
     * when there is only room for one of those it should be the one with the
     * numbers in it. The display line shrinks with it and the rules pull in.
     */
    @media (max-height:620px) {
      #threshold .sheet { padding:22px 34px 20px; }
      #threshold h1 { font-size:26px; }
      #threshold .rule { margin:15px 0 13px; }
      #threshold .blurb { display:none; }
      #threshold .ways { margin-bottom:14px; }
      #threshold .field { margin-bottom:10px; }
      #threshold .reading { margin:12px 0 0; }
      #threshold .reading td { line-height:1.7; }
      #threshold .go { margin-top:14px; }
    }
    @media (prefers-reduced-motion:reduce) {
      #threshold, #threshold .sheet { transition-duration:.01ms; } }
  `;
  document.head.appendChild(style);

  const el = document.createElement('div');
  el.id = 'threshold';
  el.innerHTML = `
    <div class="sheet">
      <h1>Unseen</h1>
      <div class="sub">a world that does not wait</div>
      <div class="rule"></div>
      <div class="ways">
        <button data-way="local" class="on">your own world</button>
        <button data-way="join">a world being kept</button>
      </div>
      <div class="blurb" data-blurb></div>
      <div data-join hidden>
        <div class="field">
          <label for="th-origin">where it is kept</label>
          <input id="th-origin" spellcheck="false" autocomplete="off"
                 placeholder="8787  ·  192.168.1.8:8787">
        </div>
        <div class="pair">
          <div class="field">
            <label for="th-token">the word you were given</label>
            <input id="th-token" spellcheck="false" autocomplete="off">
          </div>
          <div class="field">
            <label for="th-name">what to call you</label>
            <input id="th-name" spellcheck="false" autocomplete="off"
                   maxlength="${MAX_NAME_LENGTH}" placeholder="optional">
          </div>
        </div>
        <div data-reading></div>
      </div>
      <div class="go">
        <button data-enter>enter</button>
        <span class="hint" data-hint></span>
      </div>
    </div>
  `;
  document.body.appendChild(el);

  const q = <T extends HTMLElement>(sel: string) => el.querySelector(sel) as T;
  const blurb = q<HTMLDivElement>('[data-blurb]');
  const joinBox = q<HTMLDivElement>('[data-join]');
  const readingBox = q<HTMLDivElement>('[data-reading]');
  const enter = q<HTMLButtonElement>('[data-enter]');
  const hint = q<HTMLSpanElement>('[data-hint]');
  const originIn = q<HTMLInputElement>('#th-origin');
  const tokenIn = q<HTMLInputElement>('#th-token');
  const nameIn = q<HTMLInputElement>('#th-name');

  const seen = recall();
  const fromUrl = new URLSearchParams(location.search);
  originIn.value = fromUrl.get('server') ?? seen.origin ?? '';
  tokenIn.value = fromUrl.get('token') ?? seen.token ?? '';
  nameIn.value = fromUrl.get('name') ?? seen.name ?? '';

  let way: 'local' | 'join' = originIn.value ? 'join' : 'local';
  let askedForOwnWorld = false;
  const wantOwnWorld = (): void => {
    if (askedForOwnWorld) return;
    askedForOwnWorld = true;
    opts.onWantOwnWorld?.();
  };
  let reading: Reading | null = null;
  /** Rising id, so a slow probe that lands late cannot overwrite a newer one. */
  let probeId = 0;

  const LOCAL_BLURB =
    'A hundred years of prehistory have already happened here. Nobody in it ' +
    'knows you are coming, and <em>nothing will wait for you</em> once you are in.';
  const JOIN_BLURB =
    'A world someone is keeping. It has been going on without you and it will ' +
    'go on when you close this — <em>the clock there is not yours</em>.';

  const draw = (): void => {
    for (const b of el.querySelectorAll<HTMLButtonElement>('.ways button')) {
      b.classList.toggle('on', b.dataset.way === way);
    }
    blurb.innerHTML = way === 'local' ? LOCAL_BLURB : JOIN_BLURB;
    joinBox.hidden = way !== 'join';
    if (way === 'local') {
      wantOwnWorld();
      enter.disabled = false;
      hint.textContent = 'nobody else will be there';
      return;
    }
    enter.disabled = !(reading?.ok);
    hint.textContent = reading?.ok ? 'the world is open' : '';
  };

  /**
   * Ask the world what it is, and say so. Debounced, because it runs off every
   * keystroke and a request per character is rude to a server that is also
   * simulating an island.
   */
  let probeTimer: ReturnType<typeof setTimeout> | undefined;
  const probe = (): void => {
    clearTimeout(probeTimer);
    reading = null;
    draw();
    const origin = originFrom(originIn.value);
    const token = tokenIn.value.trim();
    if (!origin || !token) { readingBox.innerHTML = ''; return; }
    readingBox.innerHTML = `<div class="bad" style="color:var(--dim)">looking…</div>`;
    const mine = ++probeId;
    probeTimer = setTimeout(() => {
      void readWorld(origin, token).then((r) => {
        if (mine !== probeId) return;      // a newer probe has already answered
        reading = r;
        drawReading(r);
        draw();
      });
    }, 350);
  };

  /**
   * The reading. Every value goes in through `textContent`: a world id, a
   * label and above all another player's NAME are strings this browser did not
   * choose, and the one thing a validator upstream deliberately does not do is
   * make them safe for markup (`cleanName`).
   */
  const drawReading = (r: Reading): void => {
    readingBox.innerHTML = '';
    if (!r.ok) {
      const bad = document.createElement('div');
      bad.className = 'bad';
      bad.textContent = r.why ?? 'that world could not be read';
      readingBox.appendChild(bad);
      return;
    }
    const box = document.createElement('div');
    box.className = 'reading';
    const head = document.createElement('div');
    head.className = 'head';
    head.textContent = r.label || r.worldId || 'a world';
    box.appendChild(head);

    const table = document.createElement('table');
    const row = (k: string, v: string) => {
      const tr = document.createElement('tr');
      const kd = document.createElement('td'); kd.textContent = k;
      const vd = document.createElement('td'); vd.className = 'v'; vd.textContent = v;
      tr.append(kd, vd); table.appendChild(tr);
    };
    row('now', `${r.worldTimeText}${r.paused ? '  · held' : ''}`);
    row('age', `${(r.years ?? 0).toFixed(0)} world-years`);
    row('living', `${r.souls} souls in ${r.peoples} ${r.peoples === 1 ? 'people' : 'peoples'}`);
    row('raised', `${r.structures} structures`);
    if (r.lastSaved) row('last kept', r.lastSaved);
    const gods = r.gods ?? [];
    row('gods present', gods.length === 0 ? 'none — you would be the first'
      : gods.map((g) => g.name || g.deityId).join(', '));
    box.appendChild(table);
    readingBox.appendChild(box);
  };

  return new Promise<Entry>((resolve) => {
    let done = false;
    let onKey: (e: KeyboardEvent) => void = () => {};
    const cross = (entry: Entry): void => {
      if (done) return;
      done = true;
      remember({
        ...(originIn.value.trim() ? { origin: originIn.value.trim() } : {}),
        ...(tokenIn.value.trim() ? { token: tokenIn.value.trim() } : {}),
        ...(nameIn.value.trim() ? { name: nameIn.value.trim() } : {}),
      });
      el.classList.remove('in');
      el.classList.add('out');
      // Left on screen for the fade, then removed entirely — a threshold that
      // stayed in the DOM would keep swallowing clicks over the world.
      window.removeEventListener('keydown', onKey);
      setTimeout(() => { el.remove(); style.remove(); }, 620);
      resolve(entry);
    };

    for (const b of el.querySelectorAll<HTMLButtonElement>('.ways button')) {
      b.addEventListener('click', () => {
        way = b.dataset.way === 'join' ? 'join' : 'local';
        draw();
        if (way === 'join') { probe(); originIn.focus(); }
      });
    }
    originIn.addEventListener('input', probe);
    tokenIn.addEventListener('input', probe);

    enter.addEventListener('click', () => {
      if (way === 'local') { cross({ kind: 'local' }); return; }
      if (!reading?.ok) return;
      const name = cleanName(nameIn.value);
      cross({
        kind: 'join',
        request: {
          origin: originFrom(originIn.value),
          token: tokenIn.value.trim(),
          ...(name ? { name } : {}),
        },
      });
    });
    /*
     * Return crosses it, from anywhere — and the listener is on the WINDOW, not
     * on the sheet, which is the whole of the fix. On the sheet it only fired
     * while focus was already inside one of the fields, so a player who had
     * just loaded the page and pressed Return got nothing at all: focus is on
     * `<body>` at that point and the event never reached the element. The one
     * screen has one meaning and a person should not have to aim at it first.
     */
    onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !enter.disabled) { e.preventDefault(); enter.click(); }
    };
    window.addEventListener('keydown', onKey);

    draw();
    if (way === 'join') probe();
    requestAnimationFrame(() => el.classList.add('in'));

    // …and if this page was served BY a world, say so and point at it. Done
    // after the first draw rather than before, so the sheet arrives instantly
    // and fills itself in a moment later rather than waiting on a request.
    void servedByWorld().then((yes) => {
      if (done || !yes) return;
      if (originIn.value.trim()) { if (way === 'join') probe(); return; }
      originIn.value = location.origin;
      originIn.placeholder = location.origin;
      way = 'join';
      draw();
      probe();
      if (!tokenIn.value.trim()) tokenIn.focus(); else nameIn.focus();
    });
  });
}
